import { useInfiniteQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { Invoice } from '@/entities/invoice'
import { companyProjectsQueryOptions } from '@/entities/project'
import { uploadFile } from '@/shared/api'
import { dateInputToDay, type FileRef, flattenPages, type Kopecks, toDateInput } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input } from '@/shared/ui/field'
import { FileDrop } from '@/shared/ui/file-drop'
import { FileRow } from '@/shared/ui/file-row'
import { MoneyInput } from '@/shared/ui/money-input'
import { Select } from '@/shared/ui/select'
import { toast } from '@/shared/ui/toast'
import { dayFieldHint } from '@/shared/ui/zone-badge'
import { type IssueInvoiceTarget, useEditInvoice, useIssueInvoice } from '../api/mutations'

// Цель по контуру: проект (жёстко) или компания (по умолчанию прямой счёт, но
// можно выбрать конкретный проект компании).
type DialogTarget = { kind: 'project'; projectId: string } | { kind: 'company'; companyId: string }

interface IssueInvoiceDialogProps {
  target: DialogTarget
  open: boolean
  onClose: () => void
}

// Выставление счёта.
export function IssueInvoiceDialog({ target, open, onClose }: IssueInvoiceDialogProps) {
  const issue = useIssueInvoice()
  const [number, setNumber] = useState('')
  const [amount, setAmount] = useState<Kopecks | null>(null)
  const [due, setDue] = useState('')
  const [file, setFile] = useState<FileRef | null>(null)
  const [error, setError] = useState('')
  // Из карточки компании: '' — прямой счёт компании, иначе id выбранного проекта.
  const [projectChoice, setProjectChoice] = useState('')

  const projectsQ = useInfiniteQuery({
    ...companyProjectsQueryOptions(target.kind === 'company' ? target.companyId : ''),
    enabled: target.kind === 'company',
  })
  const companyProjects = projectsQ.data ? flattenPages(projectsQ.data.pages) : []

  function submit() {
    if (!number.trim()) return setError('Укажите номер')
    if (amount == null) return setError('Укажите сумму')
    if (!file) return setError('Загрузите файл счёта')
    setError('')
    const scope: IssueInvoiceTarget =
      target.kind === 'project'
        ? { project_id: target.projectId }
        : projectChoice
          ? { project_id: projectChoice }
          : { company_id: target.companyId }
    // Срок оплаты — календарный день "YYYY-MM-DD": ровно то, что отдаёт
    // <input type="date">.
    const dueDay = dateInputToDay(due)
    issue.mutate(
      {
        ...scope,
        number: number.trim(),
        amount_kopecks: amount,
        ...(dueDay ? { due_date: dueDay } : {}),
        file,
      },
      {
        onSuccess: () => {
          toast.success('Счёт выставлен')
          onClose()
        },
        onError: () => toast.error('Не удалось выставить счёт'),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Выставить счёт"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={issue.isPending}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={issue.isPending || !file}>
            {issue.isPending ? 'Выставляем…' : 'Выставить'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {target.kind === 'company' && (
          <Field label="Счёт по">
            {(props) => (
              <Select
                {...props}
                value={projectChoice}
                onChange={setProjectChoice}
                options={[
                  { value: '', label: 'Напрямую компании' },
                  ...companyProjects.map((d) => ({ value: d.id, label: d.title })),
                ]}
              />
            )}
          </Field>
        )}
        <InvoiceFields
          number={number}
          onNumber={setNumber}
          amount={amount}
          onAmount={setAmount}
          due={due}
          onDue={setDue}
          file={file}
          onFile={setFile}
          error={error}
        />
      </div>
    </Dialog>
  )
}

interface EditInvoiceDialogProps {
  invoice: Invoice
  open: boolean
  onClose: () => void
}

// Правка счёта — та же форма, что у выставления; контур (проект/компания) не
// меняется. Оплаченный счёт заморожен, сюда не попадает.
export function EditInvoiceDialog({ invoice, open, onClose }: EditInvoiceDialogProps) {
  const edit = useEditInvoice(invoice.id)
  const [number, setNumber] = useState(invoice.number)
  const [amount, setAmount] = useState<Kopecks | null>(invoice.amount_kopecks)
  const [due, setDue] = useState(toDateInput(invoice.due_date))
  const [file, setFile] = useState<FileRef | null>(invoice.file)
  const [error, setError] = useState('')

  function submit() {
    if (!number.trim()) return setError('Укажите номер')
    if (amount == null) return setError('Укажите сумму')
    if (!file) return setError('Загрузите файл счёта')
    setError('')
    const dueDay = dateInputToDay(due)
    edit.mutate(
      {
        number: number.trim(),
        amount_kopecks: amount,
        ...(dueDay ? { due_date: dueDay } : { clear_due_date: true }),
        // Файл шлём, только когда его заменили: перезалив того же — лишний трафик.
        ...(file.key !== invoice.file.key ? { file } : {}),
      },
      {
        onSuccess: () => {
          toast.success('Счёт обновлён')
          onClose()
        },
        onError: () => toast.error('Не удалось сохранить счёт'),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Счёт ${invoice.number}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={edit.isPending}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={edit.isPending || !file}>
            {edit.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <InvoiceFields
          number={number}
          onNumber={setNumber}
          amount={amount}
          onAmount={setAmount}
          due={due}
          onDue={setDue}
          file={file}
          onFile={setFile}
          error={error}
        />
      </div>
    </Dialog>
  )
}

interface InvoiceFieldsProps {
  number: string
  onNumber: (v: string) => void
  amount: Kopecks | null
  onAmount: (v: Kopecks | null) => void
  due: string
  onDue: (v: string) => void
  file: FileRef | null
  onFile: (f: FileRef | null) => void
  error: string
}

function InvoiceFields({
  number,
  onNumber,
  amount,
  onAmount,
  due,
  onDue,
  file,
  onFile,
  error,
}: InvoiceFieldsProps) {
  return (
    <>
      <Field label="Номер" {...(error === 'Укажите номер' ? { error } : {})}>
        {(props) => (
          <Input
            {...props}
            value={number}
            onChange={(e) => onNumber(e.target.value)}
            placeholder="СЧ-2026-003"
          />
        )}
      </Field>
      <Field label="Сумма" {...(error === 'Укажите сумму' ? { error } : {})}>
        {(props) => <MoneyInput {...props} value={amount} onChange={onAmount} placeholder="0" />}
      </Field>
      {/* Срок оплаты — календарный день: зоны у него нет, показываем ровно то,
          что уедет на бэк. */}
      <Field label="Оплатить до" hint={dayFieldHint(due)}>
        {(props) => (
          <Input {...props} type="date" value={due} onChange={(e) => onDue(e.target.value)} />
        )}
      </Field>
      <div className="flex flex-col gap-2">
        <span className="label">Файл счёта</span>
        {file ? (
          <FileRow file={file} onRemove={() => onFile(null)} />
        ) : (
          <FileDrop onUpload={uploadFile} onUploaded={onFile} />
        )}
        {error === 'Загрузите файл счёта' && <span className="text-xs text-accent">{error}</span>}
      </div>
    </>
  )
}
